import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/Button'
import { Field } from '@/components/Field'
import { APP_NAME } from '@/config'
import { cancelInvite, createInvite, formatCode, inviteLink, listInvites, type InviteRow } from '@/data/invites'
import { dateOf, fmtDate } from '@/logic/dates'
import { showToast } from '@/store/ui'
import './InviteManager.css'

function stateLine(row: InviteRow): string {
  switch (row.state) {
    case 'open':
      return `Works until ${fmtDate(dateOf(new Date(row.expiresAt)))}`
    case 'used':
      return row.householdName ? `Used: ${row.householdName}` : 'Used'
    case 'expired':
      return 'Expired'
    case 'cancelled':
      return 'Cancelled'
  }
}

/** Settings, for an admin: make an invite code for someone to start a household, and see the codes made. */
export function InviteManager() {
  const [note, setNote] = useState('')
  const [rows, setRows] = useState<InviteRow[] | null>(null)
  const [fresh, setFresh] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRows(await listInvites())
    } catch (err) {
      setError((err as Error).message)
    }
  }, [])

  useEffect(() => {
    let live = true
    listInvites().then(
      (list) => live && setRows(list),
      (err: Error) => live && setError(err.message),
    )
    return () => {
      live = false
    }
  }, [])

  const make = async () => {
    setBusy(true)
    setError(null)
    try {
      const { code } = await createInvite(note)
      setFresh(code)
      setNote('')
      await load()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const share = async (code: string) => {
    const link = inviteLink(code)
    const text = `Here's your invite to ${APP_NAME}: ${link} (code ${formatCode(code)})`
    if (navigator.share) {
      try {
        await navigator.share({ text })
        return
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
      }
    }
    try {
      await navigator.clipboard.writeText(text)
      showToast('Invite copied')
    } catch {
      showToast(`Code ${formatCode(code)}`)
    }
  }

  const cancel = async (code: string) => {
    await cancelInvite(code)
    if (fresh === code) setFresh(null)
    await load()
  }

  const freshRow = rows?.find((r) => r.code === fresh && r.state === 'open')

  return (
    <div className="settings__card invites">
      <p className="settings__line">Invite someone to start their own household. A code works once, for two weeks; they add their partner themselves.</p>
      <div className="invites__make">
        <Field label="Who it's for (optional)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={60} autoComplete="off" />
        <Button variant="primary" onClick={() => void make()} disabled={busy}>
          Make a code
        </Button>
      </div>
      {error && (
        <p className="onboard__error" role="alert">
          {error}
        </p>
      )}
      {freshRow && (
        <div className="invites__fresh">
          <span className="invites__code" aria-label={`Invite code ${freshRow.code.split('').join(' ')}`}>
            {formatCode(freshRow.code)}
          </span>
          <span className="settings__hint">
            {freshRow.note ? `For ${freshRow.note} · ` : ''}
            {stateLine(freshRow)}
          </span>
          <Button variant="secondary" onClick={() => void share(freshRow.code)}>
            Share invite
          </Button>
        </div>
      )}
      {rows && rows.length > 0 && (
        <ul className="invites__list" aria-label="Invite codes">
          {rows.map((r) => (
            <li key={r.code} className={`invites__row invites__row--${r.state}`}>
              <span className="settings__row-label">
                <span>
                  <span className="invites__mono">{formatCode(r.code)}</span>
                  {r.note && <> · {r.note}</>}
                </span>
                <span className="settings__hint">{stateLine(r)}</span>
              </span>
              {r.state === 'open' && (
                <span className="invites__actions">
                  <Button variant="quiet" onClick={() => void share(r.code)}>
                    Share
                  </Button>
                  <Button variant="quiet" onClick={() => void cancel(r.code)}>
                    Cancel
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
